import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OAuthService, ParsedIdToken, ValidationParams } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs/Observable';

interface TwoFactorAuthResponse {
	access_token: string;
	refresh_token: string;
	expires_in: number;
	error: string;
}

@Injectable()
export class ExtraauthService {

	public constructor(private oauthService: OAuthService, private _http: HttpClient) {}

	public processIdToken(idToken: string, accessToken: string): Promise<ParsedIdToken> {
		const tokenParts = idToken.split('.');
		const headerBase64 = this.padBase64(tokenParts[0]);
		const headerJson = this.b64DecodeUnicode(headerBase64);
		const header = JSON.parse(headerJson);
		const claimsBase64 = this.padBase64(tokenParts[1]);
		const claimsJson = this.b64DecodeUnicode(claimsBase64);
		const claims = JSON.parse(claimsJson);

		if (Array.isArray(claims.aud)) {
			if (claims.aud.every(v => v !== this.oauthService.clientId)) {
				const err = 'Wrong audience: ' + claims.aud.join(',');
				console.warn(err);
				return Promise.reject(err);
			}
		} else {
			if (claims.aud !== this.oauthService.clientId) {
				const err = 'Wrong audience: ' + claims.aud;
				console.warn(err);
				return Promise.reject(err);
			}
		}

		/*
		if (this.oauthService.getKeyCount() > 1 && !header.kid) {
			let err = 'There needs to be a kid property in the id_token header when multiple keys are defined via the property jwks';
			console.warn(err);
			return Promise.reject(err);
		}
		*/

		if (!claims.sub) {
			const err = 'No sub claim in id_token';
			console.warn(err);
			return Promise.reject(err);
		}

		if (!claims.iat) {
			const err = 'No iat claim in id_token';
			console.warn(err);
			return Promise.reject(err);
		}

		if (claims.iss !== this.oauthService.issuer) {
			const err = 'Wrong issuer: ' + claims.iss;
			console.warn(err);
			return Promise.reject(err);
		}

		if (!this.oauthService.disableAtHashCheck && this.oauthService.requestAccessToken && !claims['at_hash']) {
			const err = 'An at_hash is needed!';
			console.warn(err);
			return Promise.reject(err);
		}

		const now = Date.now();
		const issuedAtMSec = claims.iat * 1000;
		const expiresAtMSec = claims.exp * 1000;
		const tenMinutesInMsec = 1000 * 60 * 10;

		if (issuedAtMSec - tenMinutesInMsec >= now || expiresAtMSec + tenMinutesInMsec <= now) {
			const err = 'Token has been expired';
			console.error(err);
			console.error({
				now: now,
				issuedAtMSec: issuedAtMSec,
				expiresAtMSec: expiresAtMSec,
			});
			return Promise.reject(err);
		}

		const validationParams: ValidationParams = {
			accessToken: accessToken,
			idToken: idToken,
			jwks: this.oauthService.jwks,
			idTokenClaims: claims,
			idTokenHeader: header,
			loadKeys: () => new Promise(resolve => resolve()),
		};

		if (!this.oauthService.disableAtHashCheck && this.oauthService.requestAccessToken && !this.oauthService.tokenValidationHandler.validateAtHash(validationParams)) {
			const err = 'Wrong at_hash';
			console.warn(err);
			return Promise.reject(err);
		}

		return this.oauthService.tokenValidationHandler.validateSignature(validationParams).then(_ => {
			return {
				idToken: idToken,
				idTokenClaims: claims,
				idTokenClaimsJson: claimsJson,
				idTokenHeader: header,
				idTokenHeaderJson: headerJson,
				idTokenExpiresAt: expiresAtMSec,
			};
		});
	}

	public storeIdToken(idToken: ParsedIdToken) {
		sessionStorage.setItem('id_token', idToken.idToken);
		sessionStorage.setItem('id_token_claims_obj', idToken.idTokenClaimsJson);
		sessionStorage.setItem('id_token_expires_at', '' + idToken.idTokenExpiresAt);
		sessionStorage.setItem('id_token_stored_at', '' + Date.now());
	}

	public storeAccessTokenResponse(accessToken, refreshToken, expiresIn) {
		sessionStorage.setItem('access_token', accessToken);
		sessionStorage.setItem('access_token_stored_at', '' + Date.now());
		if (expiresIn) {
			const expiresInMilliSeconds = expiresIn * 1000;
			const now = new Date();
			const expiresAt = now.getTime() + expiresInMilliSeconds;
			sessionStorage.setItem('expires_at', '' + expiresAt);
		}
		if (refreshToken) {
			sessionStorage.setItem('refresh_token', refreshToken);
		}
	}

	public redeemTwoFactorCode(issuer: string, code: string, rememberMe: boolean = false): Observable<TwoFactorAuthResponse> {
		return this._http.post<TwoFactorAuthResponse>(`${issuer}redeem-two-factor-code`, { code, rememberMe }, {
			withCredentials: true,
			responseType: 'json',
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		}).map(resp => {
			if (resp && resp['error']) {
				throw new Error(resp['error']);
			}

			return resp;
		});
	}

	public enableTwoFactor(issuer: string, two_factor_mail: string, mail_opt_in: boolean = false): Observable<TwoFactorAuthResponse> {
		return this._http.post<TwoFactorAuthResponse>(`${issuer}enable-two-factor`, { two_factor_mail, mail_opt_in }, {
			withCredentials: true,
			responseType: 'json',
			headers: new HttpHeaders({
				'Content-Type': 'application/json'
			})
		}).map(resp => {
			if (resp && resp['error']) {
				throw new Error(resp['error']);
			}

			return resp;
		});
	}

	private padBase64(base64data): string {
		while (base64data.length % 4 !== 0) {
			base64data += '=';
		}
		return base64data;
	}

	private b64DecodeUnicode(str) {
		const base64 = str.replace(/\-/g, '+').replace(/\_/g, '/');

		return decodeURIComponent(atob(base64).split('').map(function(c) {
			return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
		}).join(''));
	}
}



// WEBPACK FOOTER //
// ./src/app/login/extraauth.service.ts